import { ContextualWarning } from 'common/contextual-warning/contextual-warning.js';
import { hasModules } from 'components.layouts/five-up/five-up-utils.js';
import { SectionContainer } from 'components/section-container.js';
import { WeatherWarningPanel } from 'components/weather-warning/warning-panel.js';
import { ModuleArrLoad } from 'layout/module-load/module-load-arr.js';
import { ModulesLazyLoadWrapper } from 'layout/module-load/modules-lazy-load-wrapper.js';
import { getModulesMetaData } from 'layout/module-load/modules-meta-data.js';
import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'redux-state/configure-store.js';
import { ILayoutConfig, IPrimarySlotPropsMap } from 'redux-state/layout/types.layout.js';
import { WarningLoadingState } from 'redux-state/weather-warnings/types.js';
import { ModulePath } from 'utilities/modulePath/modulePath.js';
import { getCurrentLocationWarnings } from 'utilities/weather-warnings/warnings.js';

interface IProps {
  config: ILayoutConfig;
  primary: IPrimarySlotPropsMap;
}

/** Used At Top Of Page To Display Warnings */
export const useLayoutHeaderSections = ({ config: { hideWarningsBar, maintenance }, primary: { first } }: IProps) => {
  const [content, setContent] = useState<JSX.Element[]>([]);
  const location = useAppSelector((s) => s.LAYOUT.screen.location);
  const warningsState = useAppSelector((state) =>
    getCurrentLocationWarnings(location ?? {}, state.WEATHER_WARNINGS.warnings),
  );

  const displayWeatherWarning = useMemo(() => {
    const forceDisplayWarningsPanel = !warningsState || warningsState?.loadState === WarningLoadingState.failed;
    const showWarningsPanel = !hideWarningsBar || forceDisplayWarningsPanel;

    if (showWarningsPanel && location) {
      return true;
    }
    return false;
  }, [hideWarningsBar, warningsState, location]);

  useEffect(() => {
    const contentArr: JSX.Element[] = [];
    if (displayWeatherWarning) {
      contentArr.push(<WeatherWarningPanel location={location} />);
    }
    if (maintenance) {
      contentArr.push(<ContextualWarning maintenance={maintenance} />);
    }
    if (hasModules(first)) {
      contentArr.push(
        <ModulesLazyLoadWrapper section={getModulesMetaData('layout-header-first', first.modules)}>
          <ModuleArrLoad modules={first.modules} modulePath={ModulePath.PRIMARY_FIRST} />
        </ModulesLazyLoadWrapper>,
      );
    }
    const finalContent = contentArr.map((jsx, i) => {
      const padding = i === contentArr.length - 1 ? 'all' : 'top';
      return (
        <SectionContainer dataModule="sections-layout-header" key={i} fill="solid" padding={padding}>
          {jsx}
        </SectionContainer>
      );
    });
    setContent(finalContent);
  }, [displayWeatherWarning, first, location, maintenance]);

  return content;
};
